// All portfolio work. Each entry gets a detail page at /work/:slug.
// `story` is an array of paragraphs. `images` are { src, caption } with paths
// relative to /images/.

export const categories = ['Furniture', 'Electronics', 'Engineering', 'Software'];

export const work = [
  {
    slug: 'split-flap-display',
    title: 'DIY Split Flap Display',
    category: 'Electronics',
    blurb: 'I designed and build a split flap display: custom PCBs, embedded firmware & frontend code, fully 3D-printed mechanics.',
    cover: 'splitflap-1.JPEG',
    youtubeId: '-C8_AtxEEQc',
    skills: ['PCB Design', 'Embedded Firmware', '3D Printing', 'CAD'],
    story: [
      "Split flap displays are the mechanical letter boards you see in old train stations. I've seen ones you can buy online, but I didn't want to spend thousands on what is essentially some cool wall art, so I built my own from the ground up. I also filmed the entire process, and the resulting video has been viewed by more than 180k people on YouTube. I published the designs and documentation on GitHub, and over 150 people have signed up for my Patreon to build my design.",
      "The driver board is a custom PCB I designed myself: an ATtiny microcontroller, RS-485 communication, a ULN2003A stepper driver, a UPDI programming interface, and more. The mechanical side is fully 3D printed — the flap drum, the motor mount, and a compact enclosure that holds it all together.",
      "Both the board and the printed parts went through several revisions before I built my display. The designs continue to improve, now with a thriving community of open-source contributors.",
    ],
    images: [
      { src: 'splitflap-1.JPEG', caption: 'Assembled split flap display, hung in my living room.' },
      { src: 'splitflap-2.JPEG', caption: 'Second batch of V2 driver boards, ready to be assembled into modules.' },
      { src: 'splitflap-3.JPEG', caption: 'Module driver board detail' },
    ],
  },
  {
    slug: 'jplay',
    title: 'J! Play',
    category: 'Software',
    blurb: 'A web app for playing along with Jeopardy! in real time — track your scores over time.',
    cover: 'jplay-1.jpg',
    liveUrl: 'https://jeopardy.adamgmakes.com',
    skills: ['React', 'JavaScript', 'Web Development', 'UI/UX'],
    story: [
      "J! Play was built because I am a huge fan of Jeopardy! and trivia in general, and wanted a way to practice and test myself without just watching the show. While it is fun to watch, it's hard for me to keep track of my own score without pausing every few clues.",
      "You can use J! Play while watching the show on TV, or just standalone on your computer. Select the clues off the board youself, use a random clue order, or play in Play-Along mode, where you follow the real contestants through the clues in the order they picked, and see how you are doing compared to them.",
      "It's live at jeopardy.adamgmakes.com.",
    ],
    images: [
      { src: 'jplay-1.jpg', caption: 'Score tracking during an episode' },
      { src: 'jplay-2.png', caption: 'Board view' },
    ],
  },
    {
    slug: 'robotic-etch-a-sketch',
    title: 'Robotic Etch A Sketch',
    category: 'Electronics',
    blurb: 'A classic Etch A Sketch converted into a CNC drawing machine with two NEMA17 steppers.',
    cover: 'etch-a-sketch.JPEG',
    skills: ['Mechatronics', 'Stepper Motors', 'Embedded Systems', '3D Printing', 'CAD'],
    story: [
      "This project turns a classic Etch A Sketch into a CNC-style drawing machine. Two NEMA17 stepper motors couple to the horizontal and vertical knobs through 3D-printed mounts, giving precise control of the stylus so the machine can render arbitrary images.",
      "I originally prototyped the drive with SimpleFOC and BLDC motors, but switched to steppers — the open-loop precision and low-speed torque are a much better fit for the slow, accurate single-line paths an Etch A Sketch demands.",
    ],
    images: [
      { src: 'etch-a-sketch.JPEG', caption: 'Motorized Etch A Sketch mid-drawing' },
    ],
  },
    {
    slug: 'robot-arm',
    title: '3D Printed 6-Axis Robot Arm',
    category: 'Engineering',
    blurb: 'A six-axis robotic arm designed in CAD and 3D printed, with a custom 4-bar linkage gripper.',
    cover: 'robotArm-1.jpg',
    skills: ['Robotics', 'Mechanical Design', '3D Printing', 'CAD', 'Programming'],
    story: [
      "A 6-axis robotic arm, fully modeled in CAD and 3D printed. Every joint, bracket, and the end effector are my own design.",
      "The gripper is a 4-bar linkage I iterated on separately — the animation below shows the closing motion straight out of the CAD model.",
    ],
    images: [
      { src: 'robotArm-1.jpg', caption: 'Printed arm assembly' },
      { src: 'robotArm-2.jpg', caption: '' },
      { src: 'CAD/robotArm-1.png', caption: 'Full arm assembly in CAD' },
      { src: 'CAD/gripper1.png', caption: 'End effector design' },
      { src: 'CAD/gripperclosing.gif', caption: 'Gripper 4-bar linkage closing' },
    ],
  },
  {
    slug: 'mcm-hutch',
    title: 'Mid-Century Walnut Hutch',
    category: 'Furniture',
    blurb: 'A solid walnut room divider / hutch with curved 90° shelves. Three months of work; sold for $3,000.',
    cover: 'mcmHutch-2.JPEG',
    skills: ['Woodworking', 'Furniture Design', 'CAD'],
    story: [
      "I'm still not sure what to call this piece — hutch, room divider, cabinet, bookshelf. It does all of those jobs. It started as Pinterest inspiration, but the actual design is mine, modeled in Fusion 360 and built in solid walnut over about three months in 2024.",
      "My favorite details are the curved 90-degree shelves that wrap the corners, and the variable-sized shelf bays on the upper section. It sold for $3,000.",
    ],
    images: [
      { src: 'mcmHutch-2.JPEG', caption: 'Finished hutch' },
      { src: 'mcmHutch-3.JPEG', caption: '' },
      { src: 'mcmHutch-4.JPEG', caption: 'Curved corner shelf detail' },
      { src: 'mcmHutch-5.JPEG', caption: '' },
      { src: 'mcmHutch-6.JPEG', caption: '' },
      { src: 'mcmHutch-7.JPEG', caption: '' },
      { src: 'mcmHutch-1.JPEG', caption: '' },
      { src: 'mcmHutch-8.JPEG', caption: 'In progress' },
      { src: 'mcmHutch-9.JPEG', caption: 'In progress' },
      { src: 'mcmHutch-10.JPEG', caption: '' },
      { src: 'mcmHutch-11.JPEG', caption: '' },
    ],
  },

  {
    slug: 'wave-mirror',
    title: 'Patterned Plywood Wave Mirror',
    category: 'Furniture',
    blurb: 'Laminated Baltic birch, CNC-cut into a wave and power-carved at the edges.',
    cover: 'waveMirror-4.jpg',
    skills: ['Woodworking', 'Patterned Plywood', 'CNC', 'Power Carving', 'CAD'],
    story: [
      "Inspired by Michael Alm's patterned plywood work — precisely cut strips of Baltic birch laminated into a repeating pattern. This piece actually started life as a desktop, and pivoted mid-project when I admitted that would be entirely too large to ever finish.",
      "Instead I CNC-cut the wave shape and power-carved relief into the edges. I like the tension in it: the pattern is all straight lines and sharp corners, the overall form is soft and smooth. It hangs in my home, and I want to iterate on the concept again.",
    ],
    images: [
      { src: 'waveMirror-4.jpg', caption: 'Finished wave mirror' },
      { src: 'waveMirror-1.jpg', caption: '' },
      { src: 'waveMirror-2.jpg', caption: 'Pattern detail' },
      { src: 'waveMirror-3.jpg', caption: '' },
      { src: 'waveMirror-5.jpg', caption: 'Carved edge relief' },
      { src: 'waveMirror-6.jpg', caption: '' },
    ],
  },
  {
    slug: 'walnut-bookshelf',
    title: 'Walnut Bookshelf',
    category: 'Furniture',
    blurb: 'Six shelves of solid walnut, designed in Fusion 360 to swallow a full wall of books.',
    cover: 'bookshelf-1.JPEG',
    skills: ['Woodworking', 'Furniture Design', 'CAD'],
    story: [
      "A six-shelf solid walnut bookshelf, designed from scratch in Fusion 360 and built to hold a serious book collection — hundreds of books across its shelves.",
      "The construction is clean through-dado joinery, solid walnut sides and shelves, finished to bring out the grain. It lives in a corner against an exposed brick wall, which it was sized for.",
    ],
    images: [
      { src: 'bookshelf-1.JPEG', caption: 'Loaded and in place' },
      { src: 'bookshelf-2.JPEG', caption: '' },
      { src: 'bookshelf-3.JPEG', caption: 'Joinery detail' },
    ],
  },
  {
    slug: 'river-nightstand',
    title: 'Walnut River Nightstand',
    category: 'Furniture',
    blurb: 'A live-edge walnut nightstand with a resin river top, designed in CAD and cut on the CNC.',
    cover: 'nightstand-1.jpg',
    skills: ['Woodworking', 'Resin', 'CNC', 'CAD'],
    story: [
      "A nightstand built around a walnut-and-resin river top. The piece was modeled in Fusion 360 first, and the CNC handled flattening and the joinery-critical cuts before the resin pour and finish work.",
    ],
    images: [
      { src: 'nightstand-1.jpg', caption: 'Finished nightstand' },
      { src: 'nightstand-2.jpg', caption: '' },
      { src: 'nightstand-3.jpg', caption: 'River top detail' },
      { src: 'nightstand-4.jpg', caption: '' },
      { src: 'nightstand-5.jpg', caption: '' },
      { src: 'CAD/nightstand.png', caption: 'Fusion 360 model' },
    ],
  },
  {
    slug: 'mid-century-chair',
    title: 'Mid-Century Chair',
    category: 'Furniture',
    blurb: 'A mid-century inspired lounge chair — CNC-templated frame, hand-finished, leather seat.',
    cover: 'chair-5.jpg',
    skills: ['Woodworking', 'Leather Working', 'CNC', 'CAD'],
    story: [
      "A mid-century inspired chair, and the project that pushed me into combining disciplines: the frame was templated on the CNC from a Fusion 360 model, shaped and finished by hand, and upholstered with leather I worked myself.",
    ],
    images: [
      { src: 'chair-5.jpg', caption: 'Finished chair' },
      { src: 'chair-1.JPEG', caption: '' },
      { src: 'chair-2.JPEG', caption: '' },
      { src: 'chair-3.jpg', caption: '' },
      { src: 'chair-4.JPEG', caption: '' },
    ],
  },

  {
    slug: 'nesting-side-tables',
    title: 'Nesting Side Tables',
    category: 'Furniture',
    blurb: 'A pair of nesting side tables that tuck into each other when not in use.',
    cover: 'nestSideTables-2.JPEG',
    skills: ['Woodworking', 'CNC', 'CAD'],
    story: [
      "A pair of nesting side tables — sized so one slides under the other when they're not both needed. Designed in Fusion 360, with the curved cuts templated on the CNC and the rest done at the bench.",
    ],
    images: [
      { src: 'nestSideTables-2.JPEG', caption: 'The pair, nested' },
      { src: 'nestSideTables-1.JPEG', caption: '' },
      { src: 'nestSideTables-3.JPEG', caption: '' },
      { src: 'nestSideTables-4.JPEG', caption: '' },
    ],
  },
  
  {
    slug: 'nitinol-bone-plate',
    title: 'Nitinol Bone Fixation Plate',
    category: 'Engineering',
    blurb: 'Senior design: a shape-memory nitinol plate that actively compresses bone fractures as it warms to body temperature.',
    cover: 'SeniorDesign/nitinol-2.JPEG',
    skills: ['Biomedical Design', 'Nitinol / Shape-Memory Alloys', 'CAD', 'Prototyping', 'Materials Testing'],
    story: [
      "My senior design project in biomedical engineering: a bone fixation plate machined from nitinol, a shape-memory alloy. The idea is that the plate is deformed while cold, fixed across a fracture, and then — as it warms to body temperature — tries to return to its original shape, applying steady compression across the fracture site to promote healing.",
      "We started by characterizing the mechanical properties of nitinol ourselves, then iterated through aluminum prototypes to prove out the geometry — the arms of the plate are designed so that bending them produces compression between the screw holes. From there we cut the first plates from nitinol sheet and had final prototypes manufactured.",
      "Alongside the plate, the same team built an enteral feeding tube cleaner prototype — a device for clearing clogged feeding tubes in place rather than replacing them.",
    ],
    images: [
      { src: 'SeniorDesign/nitinol-1.JPEG', caption: 'Testing the mechanical properties of nitinol' },
      { src: 'SeniorDesign/nitinol-2.JPEG', caption: 'Preparing to cut the first plate from nitinol sheet' },
      { src: 'SeniorDesign/nitinol-3.JPEG', caption: 'Aluminum prototype showing compression as the arms are bent' },
      { src: 'SeniorDesign/nitinol-4.JPEG', caption: 'Early aluminum geometry prototypes' },
      { src: 'SeniorDesign/nitinol-5.JPEG', caption: 'Receiving finished prototypes from the manufacturer' },
      { src: 'SeniorDesign/nitinol-6.JPEG', caption: 'Prototype detail' },
      { src: 'SeniorDesign/nitinolplate-1.png', caption: 'CAD model of the final plate design' },
      { src: 'SeniorDesign/nitinolplate-2.png', caption: 'Final plate with applicator attached' },
      { src: 'SeniorDesign/nitinolplate-3.png', caption: 'Early plate design with applicator' },
    ],
    videos: [
      { src: 'SeniorDesign/nitinol-vid-1.MP4', caption: 'First rough nitinol prototype showing shape memory under heating and cooling' },
      { src: 'SeniorDesign/tubecleaner-vid-1.MP4', caption: 'Enteral feeding tube cleaner prototype' },
    ],
  },
];

// Smaller builds, process shots, and CAD work — shown as a photo wall on the
// home page rather than getting full project pages.
export const shopShots = [
  { src: 'leatherPurse-1.JPEG', caption: 'Hand-stitched leather purse' },
  { src: 'leatherPurse-3.JPEG', caption: 'Leather purse detail' },
  { src: 'oakTable-1.JPEG', caption: 'Oak table' },
  { src: 'oakTable-3.JPEG', caption: 'Oak table' },
  { src: 'brickBoard-1.JPEG', caption: 'Brick-pattern cutting board' },
  { src: 'brickBoard-3.JPEG', caption: 'Brick-pattern cutting board' },
  { src: 'woodworking/cuttingBoard-1.JPEG', caption: 'Geometric cutting board' },
  { src: 'woodworking/bedFrame-1.JPEG', caption: 'Custom bed frame' },
  { src: 'woodworking/bendyLamp.JPEG', caption: 'Power-carved lamp' },
  { src: 'woodworking/wallhooks-1.JPEG', caption: 'Wooden wall hooks' },
  { src: 'woodworking/IMG_9476.JPEG', caption: 'Media stand built for friends' },
  { src: 'SLSglass-1.JPEG', caption: 'Stained glass — SLS rocket' },
  { src: 'SLSglass-2.JPEG', caption: 'Stained glass — SLS rocket' },
  { src: 'CAD/cam1.png', caption: '35mm film camera CAD model (in progress)' },
  { src: 'CAD/bedset1.png', caption: 'Bedroom set CAD model' },
  { src: 'CAD/spiralTable.png', caption: 'Spiral table concept' },
  { src: 'CAD/bottlecapcatcherCAD.png', caption: 'Bottle cap catcher CAD model' },
];

// NASA internship photos, shown in the background strip on the home page.
export const nasaShots = [
  { src: 'NASA/missioncontrol.JPEG', caption: 'ISS Mission Control' },
  { src: 'NASA/NBL-1.JPEG', caption: 'With an astronaut ready to train at the Neutral Buoyancy Lab' },
  { src: 'NASA/NBL-3.JPEG', caption: 'An astronaut being lowered into the NBL pool' },
  { src: 'NASA/gene.JPEG', caption: 'With Gene Kranz, Apollo 11 flight director' },
  { src: 'NASA/robonaut.JPEG', caption: 'Meeting Robonaut' },
  { src: 'NASA/psion.JPEG', caption: 'The PSION lab, where I worked — MED-2 on the floor' },
  { src: 'NASA/ARED-1.JPEG', caption: 'ARED — one of two in existence; the other is on the ISS' },
  { src: 'NASA/flying.JPG', caption: 'Flying at night over Houston with my mentor' },
];
